/* eslint-disable complexity */
import { pathOr } from 'ramda';
import React, { memo, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { emptyImageTransparentUrl } from 'reactApp/constants';
import { getMyOfficeTouchFeature } from 'reactApp/modules/features/features.selectors';
import {
    getItemNameWithoutExt,
    getWeblinkFromPublicId,
    isArchive,
    isArchiveZip,
    isAudio,
    isDocument,
    isImage,
    isPdf,
    isPlainSource,
    isPreviewable,
    isVideo,
    isVirusItem,
} from 'reactApp/modules/file/utils';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isCloudFile } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { Button } from 'reactApp/ui/Button/Button';
import { MobileViewerArchive } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerArchive/MobileViewerArchive';
import { MobileViewerImg } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerItem/MobileViewerImg';
import { MobileViewerTxt } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerTxt/MobileViewerTxt';
import { ReactPdfViewer } from 'reactApp/ui/ReactViewer/ReactPdfViewer/ReactPdfViewer';
import { getItemImageUrl, getPosterUrl, sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { Stub } from 'reactApp/ui/ReactViewer/Stub/Stub';
import { VideoPlayer } from 'reactApp/ui/ReactViewer/VideoPlayer/VideoPlayer';
import { noop } from 'reactApp/utils/helpers';
import { useSwiper } from 'swiper/react';

import { MobileViewerDoc } from '../MobileViewerDoc/MobileViewerDoc';
import styles from './MobileViewerItem.css';

interface Props {
    itemId: string;
    sendAnalytics?(data: any): void;
    isSingle?: boolean;
    handleDownload?(): void;
    selected?: boolean;
    setShowCustomItem?: (state: boolean) => void;
    openToolbarOptions?: () => void;
}

export const MobileViewerItem = memo<Props>(
    ({ openToolbarOptions, setShowCustomItem, itemId, isSingle, handleDownload = noop, selected = true }): ReactElement | null => {
        const storage = useSelector(getCurrentStorage);
        const item = useSelector((state: RootState) => getStorageItemById(state, storage, itemId));
        const isPreviewableItem = item && isPreviewable(item);
        const [showArchive, setShowArchive] = useState(false);
        const swiper = useSwiper();
        const myOfficeTouchFeature = useSelector(getMyOfficeTouchFeature);
        const showMyOfficeItem = myOfficeTouchFeature && isCloudFile(item) && isDocument(item) && selected;
        const source = IS_PUBLIC_ALBUM ? 'album' : storage;

        useEffect(() => {
            if (selected && item && !item.isFolder) {
                sendViewerDwh({
                    id_public: storage === EStorageType.public ? getWeblinkFromPublicId(item.id) : 'None',
                    source: source || '',
                    type_content: item.kind,
                    extension: item.ext?.toLowerCase() || '',
                    action: 'open-content',
                    is_stories: storage === EStorageType.story,
                    have_face: false,
                    is_full_render: isPreviewableItem,
                    is_edit: false,
                    size_files: item.size,
                });
            }
        }, [selected, item]);

        const name = item?.name || '';
        const ext = pathOr('', ['ext'], item);
        const nameWithoutExt = getItemNameWithoutExt(name, ext);
        const mediaUrl = pathOr('', ['url', 'media'], item);
        const viewUrl = pathOr('', ['url', 'view'], item);
    const getUrl = pathOr('', ['url', 'get'], item);

        const isVirus = isVirusItem(item);
        const isItemArchive = item && isArchive(item) && isArchiveZip(item);

        if (isItemArchive && selected && showArchive) {
            return (
                <MobileViewerArchive
                    openToolbarOptions={openToolbarOptions}
                    onShow={() => {
                        /**
                         * Этот метод скрывает тулбар просмотрщика,
                         * чтобы иметь возможность отрисовать на весь экран свой компонент для архива
                         *
                         * Возвращаем на onClose
                         */
                        setShowCustomItem?.(true);
                        /**
                         * отключаем свайпалку для просмотрщика архива
                         *
                         * Возвращаем на onClose
                         */
                        swiper.disable();
                    }}
                    onClose={() => {
                        setShowArchive(false);
                        setShowCustomItem?.(false);
                        swiper.enable();
                    }}
                    storage={storage}
                    item={item}
                />
            );
        }

        if (item && isPreviewableItem && isPdf(item)) {
        return <ReactPdfViewer url={getUrl || viewUrl} />;
    }

    if (showMyOfficeItem) {
        return <MobileViewerDoc file={item} />;
    }

        if (isPreviewableItem && isPlainSource(item, storage)) {
            return <MobileViewerTxt item={item} />;
        }

        if (isPreviewableItem && isImage(item)) {
            const src = getItemImageUrl(item);

            return <MobileViewerImg src={src} name={name} ext={ext} kind={item?.kind} isVirus={isVirus} />;
        }

        if (isPreviewableItem && (isVideo(item) || isAudio(item))) {
            const poster = getPosterUrl(item);

            return (
                <VideoPlayer
                    streamUri={mediaUrl}
                    nativeUrl={viewUrl}
                    posterUrl={isAudio(item) ? emptyImageTransparentUrl : poster}
                    kind={item.kind}
                    ext={ext}
                    name={name}
                    isVisible={selected}
                    size={'size' in item ? item.size : undefined}
                />
            );
    }

        return (
            <div className={styles.iconBlock}>
                <Stub
                    ext={ext}
                    isVirus={isVirus}
                    name={nameWithoutExt}
                    kind={item?.kind || 'file'}
                    mode={isSingle ? 'light' : 'dark'}
                    size={isSingle ? 'medium' : 'big'}
                    showName={isSingle}
                    onClick={handleDownload}
                />
                {isItemArchive && (
                    <div className={styles.iconBlock_footer}>
                        <Button onClick={() => setShowArchive(true)} theme="octavius" primary className={styles.iconBlock_footerButton}>
                            Открыть архив
                        </Button>
                    </div>
                )}
            </div>
        );
    }
);

MobileViewerItem.displayName = 'MobileViewerItem';
